import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

const browser = (ua = "") => {
  const m = ua.match(/(Edg|OPR|Chrome|Firefox|Version)\/([\d.]+)/);
  const name = m ? { Edg: "Edge", OPR: "Opera", Version: "Safari" }[m[1]] || m[1] : "Unknown";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return `${name}${m ? " " + m[2].split(".")[0] : ""}${os ? " · " + os : ""}`;
};

export function ErrorsPanel() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/client-errors").then(({ data }) => setErrors(data)).catch(() => toast.error("Failed to load errors")).finally(() => setLoading(false));
  }, []);

  const clearAll = async () => {
    if (!window.confirm("Clear all recorded errors?")) return;
    try {
      await adminApi.delete("/admin/client-errors");
      setErrors([]);
      toast.success("Error log cleared");
    } catch {
      toast.error("Could not clear errors");
    }
  };

  return (
    <div data-testid="errors-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Errors visitors' browsers hit on the live site. You're emailed the first time each new error appears (max once per hour).</p>
        <Button variant="outline" onClick={clearAll} disabled={!errors.length} data-testid="errors-clear-all"><Trash2 className="h-4 w-4 mr-2" />Clear all</Button>
      </div>
      <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="errors-table">
            <thead className="bg-brand-50/60 text-brand text-left">
              <tr>{["Date", "Page", "Browser", "Error"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {errors.map((e, i) => (
                <React.Fragment key={i}>
                  <tr onClick={() => setOpen(open === i ? null : i)} className="border-t border-brand/5 align-top cursor-pointer hover:bg-brand-50/40" data-testid="error-row">
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                    <td className="px-4 py-3 break-all max-w-[220px]">{(e.url || "").replace(/^https?:\/\/[^/]+/, "") || "/"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{browser(e.ua)}</td>
                    <td className="px-4 py-3 text-red-600 break-all">{e.message}</td>
                  </tr>
                  {open === i && (
                    <tr className="bg-brand-50/30"><td colSpan={4} className="px-4 py-3">
                      <div className="text-xs text-muted-foreground mb-1">Source: {e.source} · {e.ua}</div>
                      <pre className="text-xs whitespace-pre-wrap break-all max-h-64 overflow-auto">{e.stack || e.extra || "No stack trace"}</pre>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {loading && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!errors.length && !loading && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground" data-testid="errors-empty">No errors recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
